import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CalendarClock, LayoutList, CalendarRange, Search } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EmptyState, Panel, StatCard, StatusBadge, formatDate } from "@/components/admin/ui";
import DemoRequestDrawer from "@/components/admin/DemoRequestDrawer";
import DemoCalendar from "@/components/admin/DemoCalendar";
import { INDUSTRIES } from "@/data/industries";
import { usePolling } from "@/hooks/usePolling";
import { usePageLastUpdated } from "@/context/PageMetaContext";

const STATUSES = ["Pending", "Contacted", "Scheduled", "Completed", "Declined"];

export default function AdminDemoRequests() {
  const { API } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [industry, setIndustry] = useState(searchParams.get("industry") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [view, setView] = useState(searchParams.get("view") === "calendar" ? "calendar" : "list");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const focusParam = searchParams.get("focus") || "";

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    if (!silent) setError("");
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (industry) params.industry = industry;
      if (status) params.status = status;
      const { data } = await axios.get(`${API}/admin/demo-requests`, { params, withCredentials: true });
      setRequests(data.demo_requests || []);
      setStatusCounts(data.status_counts || {});
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [API, search, industry, status]);

  useEffect(() => {
    const timer = window.setTimeout(load, search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  usePolling(() => load(true), 10000);

  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (industry) params.industry = industry;
    if (status) params.status = status;
    if (focusParam) params.focus = focusParam;
    setSearchParams(params, { replace: true });
  }, [search, industry, status, focusParam, setSearchParams]);

  useEffect(() => {
    if (!focusParam || selected?.demo_request_id === focusParam) return;
    axios
      .get(`${API}/admin/demo-requests/${focusParam}`, { withCredentials: true })
      .then(({ data }) => setSelected(data.demo_request))
      .catch(() => setError("The requested Demo Request could not be found."));
  }, [API, focusParam, selected]);

  const closeDrawer = () => {
    setSelected(null);
    if (focusParam) {
      const params = {};
      if (search) params.search = search;
      if (industry) params.industry = industry;
      if (status) params.status = status;
      setSearchParams(params, { replace: true });
    }
  };

  const updated = (request) => {
    setRequests((rows) => rows.map((row) => row.demo_request_id === request.demo_request_id ? request : row));
    load();
  };

  usePageLastUpdated(lastUpdated, "demo-requests-last-updated");

  return (
    <div className="space-y-6" data-testid="admin-demo-requests-page">
      <div className="flex justify-end" data-testid="demo-view-switch">
        <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm dark:border-white/10 dark:bg-[#111C33]">
          <button
            type="button"
            onClick={() => setView("list")}
            data-testid="demo-view-list-btn"
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition-colors duration-200 ${view === "list" ? "bg-[#0052FF] text-white" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10"}`}
          >
            <LayoutList size={15} /> List view
          </button>
          <button
            type="button"
            onClick={() => setView("calendar")}
            data-testid="demo-view-calendar-btn"
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition-colors duration-200 ${view === "calendar" ? "bg-[#0052FF] text-white" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10"}`}
          >
            <CalendarRange size={15} /> Calendar view
          </button>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="demo-request-summary-cards">
        {STATUSES.slice(0, 4).map((item, index) => (
          <StatCard
            key={item}
            icon={CalendarClock}
            label={item}
            value={statusCounts[item] || 0}
            helper="Demo requests"
            tone={["orange", "blue", "purple", "green"][index]}
            testId={`demo-summary-${item.toLowerCase()}`}
          />
        ))}
      </div>

      <Panel testId="demo-requests-table-panel">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="demo-status-tabs">
            {["All", ...STATUSES].map((item) => {
              const value = item === "All" ? "" : item;
              const active = status === value;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => setStatus(value)}
                  data-testid={`demo-filter-${item.toLowerCase()}`}
                  className={`rounded-full px-4 py-2 text-sm font-bold transition-colors duration-200 ${active ? "bg-[#0052FF] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"}`}
                >
                  {item}
                </button>
              );
            })}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            {view === "list" && (
              <label className="relative block">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="demo-search-input"
                  placeholder="Search company, contact, email…"
                  className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 sm:w-72 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
                />
              </label>
            )}
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              data-testid="demo-industry-filter"
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white"
            >
              <option value="">All industries</option>
              {INDUSTRIES.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
        </div>

        {error && <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="demo-admin-error">{error}</p>}

        <div className="mt-6">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="demo-requests-loading">Loading demo requests…</div>
          ) : view === "calendar" ? (
            <DemoCalendar requests={requests} onSelect={setSelected} />
          ) : requests.length === 0 ? (
            <EmptyState icon={CalendarClock} title="No demo requests yet." message="Requests submitted through the public Request Demo form will appear here." testId="demo-requests-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-left text-sm" data-testid="admin-demo-request-list">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.12em] text-slate-400 dark:border-white/10 dark:text-slate-500">
                    <th className="py-3 pr-4 font-bold">Company</th>
                    <th className="py-3 pr-4 font-bold">Contact</th>
                    <th className="py-3 pr-4 font-bold">Work Email</th>
                    <th className="py-3 pr-4 font-bold">Role</th>
                    <th className="py-3 pr-4 font-bold">Industry</th>
                    <th className="py-3 pr-4 font-bold">Submitted</th>
                    <th className="py-3 pr-4 font-bold">Status</th>
                    <th className="py-3 font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => (
                    <tr key={request.demo_request_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 dark:border-white/5 dark:hover:bg-white/5" data-testid={`demo-row-${request.demo_request_id}`}>
                      <td className="py-4 pr-4 font-bold text-slate-950 dark:text-white">{request.company}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{request.name}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{request.email}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{request.role || "—"}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{request.industry}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{formatDate(request.submitted_at)}</td>
                      <td className="py-4 pr-4"><StatusBadge status={request.status} testId={`demo-row-status-${request.demo_request_id}`} /></td>
                      <td className="py-4"><button type="button" onClick={() => setSelected(request)} data-testid={`demo-view-${request.demo_request_id}`} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-white dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Panel>

      <DemoRequestDrawer
        request={selected}
        open={!!selected}
        onClose={closeDrawer}
        onUpdated={updated}
      />
    </div>
  );
}
